import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonToggle,
  useIonRouter,
} from '@ionic/react';
import type { InfiniteScrollCustomEvent } from '@ionic/core';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar } from '../components/ui/TopBar';
import { useCollapsingTitle } from '../hooks/useCollapsingTitle';
import { PullToRefresh } from '../components/ui/PullToRefresh';
import { OrderHistoryRow } from '../components/orders/OrderHistoryRow';
import { useMyOrders } from '../hooks/useOrders';

/**
 * My Orders — buyer-side history of orders the caller placed on other listers'
 * dishes. Cursor-paginated (infinite scroll), newest-first. The "Show
 * incomplete" switch drives the server `include` filter: off (default) hides
 * never-paid orders (reservations / lapsed checkouts); on returns everything.
 * Filtering is server-side so it stays consistent across pages.
 */
export const MyOrdersPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const router = useIonRouter();
  const queryClient = useQueryClient();
  const [showIncomplete, setShowIncomplete] = useState(false);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMyOrders(showIncomplete);
  const myOrders = data?.pages.flat() ?? [];

  const contentRef = useRef<HTMLIonContentElement>(null);
  // Prefix-invalidate both `include` variants so the refreshed list is right
  // whichever way the Show-incomplete switch is set. Stable identity via
  // useCallback — PullToRefresh rebinds its gesture when `onRefresh` changes.
  const handleRefresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['my-orders'] }),
    [queryClient],
  );

  // Title band hides on scroll-down, returns on scroll-up.
  const { collapsed, onScroll } = useCollapsingTitle(contentRef);

  return (
    <IonPage>
      <TopBar title={t('menu.orders')} titleCollapsed={collapsed} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
        <PullToRefresh contentRef={contentRef} onRefresh={handleRefresh}>
        <div className="mx-auto max-w-lg px-4 pt-4 pb-2 space-y-4">
          {/* Show-incomplete filter. Always offered — with server-side paging
              we can't cheaply know whether hidden orders exist, and this is
              the only path to reveal an account whose orders are all
              incomplete. */}
          <div className="flex items-center justify-end gap-2">
            <span className="text-sm text-muted-foreground">
              {t('orders.showIncomplete')}
            </span>
            <IonToggle
              checked={showIncomplete}
              onIonChange={(e) => setShowIncomplete(e.detail.checked)}
            />
          </div>

          {myOrders.length > 0 ? (
            <div>
              {myOrders.map((order) => (
                <OrderHistoryRow
                  key={order.orderId}
                  order={order}
                  onClick={() => router.push(`/orders/${order.orderId}`)}
                />
              ))}
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {/* Off + empty nudges toward the toggle; on + empty means there's
                  genuinely nothing. */}
              {showIncomplete ? t('orders.empty') : t('orders.emptyFiltered')}
            </p>
          )}
        </div>

        <IonInfiniteScroll
          disabled={!hasNextPage}
          onIonInfinite={async (ev: InfiniteScrollCustomEvent) => {
            if (hasNextPage && !isFetchingNextPage) {
              await fetchNextPage();
            }
            ev.target.complete();
          }}
        >
          <IonInfiniteScrollContent loadingSpinner="crescent" />
        </IonInfiniteScroll>
        </PullToRefresh>
      </IonContent>
    </IonPage>
  );
};
